import { Box, Stack, Typography } from "@mui/material";
import { Theme } from "@mui/material/styles";
import type { JSX } from "react";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";

import { fileToBase64String, uploadDropFileFilter } from "@/helpers/fileHelpers";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getMainContainerStyles = (hasValue: boolean, height?: string, disabled?: boolean, hasError?: boolean) =>
  ((theme: Theme) => ({
    height: height || "190px",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: disabled ? theme.semanticColors.surface.disabled : theme.semanticColors.background.paper,
    border: `1px dashed ${
      hasError
        ? theme.semanticColors.feedback.error.destructive
        : disabled
          ? theme.semanticColors.border.disabled
          : theme.semanticColors.border.action
    }`,
    borderRadius: "12px",
    padding: "16px",
    cursor: hasValue || disabled ? "default" : "pointer",
    opacity: disabled ? 0.6 : 1,
  })) as never;

const styles = {
  uploadTextMain: {
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "20px",
    color: (theme: Theme): string => theme.semanticColors.interactive.primaryStrong,
  },
  uploadTextSub: {
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "20px",
    color: (theme: Theme): string => theme.semanticColors.text.muted,
  },
  uploadTextTypes: {
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "18px",
    color: (theme: Theme): string => theme.semanticColors.text.muted,
  },
  errorText: {
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "18px",
    color: (theme: Theme): string => theme.semanticColors.feedback.error.destructive,
    paddingTop: "14px",
  },
};

interface ImageUploadProps {
  titleMain: string;
  titleSub: string;
  uploadTypesText: string;
  removeImageText: string;
  uploadIcon: React.ReactNode;
  value: string | null | undefined;
  onImageUpload: (value: string | null) => void;
  onPendingChange?: (value: boolean) => void;
  height?: string;
  disabled?: boolean;
  maxFileSize?: number;
  error?: string | null;
}

export const ImageUpload = ({
  titleMain,
  titleSub,
  uploadTypesText,
  uploadIcon,
  removeImageText,
  value,
  onImageUpload,
  onPendingChange,
  height,
  disabled,
  maxFileSize = 5,
  error,
}: ImageUploadProps): JSX.Element => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const maxFileSizeBytes = maxFileSize * 1024 * 1024;
  const supportedFileTypes = {
    "image/png": [".png"],
    "image/jpeg": [".jpeg", ".jpg"],
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: async (acceptedFiles, rejectedFiles) => {
      if (disabled) return;

      // Clear previous error
      setErrorMessage(null);

      // Check for rejected files
      if (rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0];
        if (rejection.errors.some((error) => error.code === "file-too-large")) {
          setErrorMessage(t("errors.images.invalidFileSize", { maxFileSize }));
          return;
        }
        if (rejection.errors.some((error) => error.code === "file-invalid-type")) {
          setErrorMessage(t("errors.images.invalidFileType"));
          return;
        }
      }

      const filteredFiles = uploadDropFileFilter(acceptedFiles, supportedFileTypes, maxFileSizeBytes);
      if (filteredFiles.length === 1) {
        onPendingChange?.(true);
        try {
          const base64String = await fileToBase64String(filteredFiles[0]);
          onImageUpload(base64String);
          setErrorMessage(null);
        } finally {
          onPendingChange?.(false);
        }
      }
    },
    maxFiles: 1,
    maxSize: maxFileSizeBytes,
    accept: supportedFileTypes,
    noKeyboard: true,
    multiple: false,
    disabled,
  });

  const handleRemove = (): void => {
    onImageUpload(null);
    setErrorMessage(null);
  };

  const displayError = error || errorMessage;

  return (
    <Box
      {...(value ? {} : getRootProps())}
      sx={getMainContainerStyles(!!value, height, disabled, !!displayError)}
      data-cy="image-upload-box"
    >
      <input {...getInputProps()} style={{ display: "none" }} />
      {!value && (
        <Stack alignItems={"center"} direction="column" gap="12px" minWidth={"245px"}>
          {uploadIcon}
          <Stack alignItems={"center"} direction="column" gap="4px">
            <Stack alignItems={"start"} direction="row" gap="5px" flexWrap="wrap" useFlexGap justifyContent="center">
              <Typography sx={styles.uploadTextMain}>{titleMain}</Typography>
              <Typography sx={styles.uploadTextSub}>{titleSub}</Typography>
            </Stack>
            <Typography sx={styles.uploadTextTypes}>{uploadTypesText}</Typography>
            {displayError && <Typography sx={styles.errorText}>{displayError}</Typography>}
          </Stack>
        </Stack>
      )}
      {value && (
        <Stack alignItems="center" direction="column" gap="8px" minWidth={"245px"}>
          <Box
            component="img"
            src={value}
            alt="Uploaded preview"
            sx={{
              maxWidth: "100%",
              maxHeight: "120px",
              objectFit: "contain",
              pointerEvents: "none",
            }}
          />
          <Typography
            sx={styles.uploadTextMain}
            onClick={(e) => {
              if (disabled) return;
              e.stopPropagation();
              handleRemove();
            }}
          >
            {removeImageText}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};
